import '../../assets/styles/auth.scss'
import '../../assets/styles/pages.scss'
import laptop from '../../assets/images/pages/laptop.jpg'
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom'
import { Fragment, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react'
import DownloadFileModal from '../../components/common/download-file-modal'
import { GlobalContext, handleError } from '../../context/Provider'
import henceforthApi from '../../utils/henceforthApi'
import moment from 'moment'
import BreadCrumb from '../../components/common/BreadCrumb'
import { numberWithCommas } from '../../utils/validations'
import { ProductList } from './productInterface'
import { toast } from 'react-toastify'
import AgTable from '../../components/common/AgTable'
import ProductCell from '../../components/common/ProductCell'
import StockBadge from '../../components/common/StockBadge'
import ActionCell from '../../components/common/ActionCell'
import { ColDef } from 'ag-grid-community'
import {
    Search,
    X,
    Plus,
    Download,
    Copy,
    Check,
    DollarSign,
    RotateCcw,
    ChevronDown,
} from 'lucide-react'

const Products = () => {
    const match: any = useMatch('/products/:page')
    const navigate = useNavigate()
    const location = useLocation()
    const { authState, loading, setLoading, authDispatch, language_data, handleSearch, onFilterPriceHandler } = useContext(GlobalContext)

    let breadCrumbPath = [
        { name: `${language_data?.[authState.lang]?.common_home || 'Home'}`, url: `/`, active: '' },
        { name: `${language_data?.[authState.lang]?.common_product || 'Products'} ${language_data?.[authState.lang]?.common_list || 'List'}`, url: ``, active: 'not-allowed' }
    ]
    const Limit = 10;
    const newParam = useMemo(() => new URLSearchParams(location.search), [location.search]);

    const [productList, setProductList] = useState<ProductList>({
        totalCount: 0,
        listing: [],
    })

    // Local search state for smooth debounced input
    const [searchTerm, setSearchTerm] = useState(newParam.get('search') || '')
    const [copiedId, setCopiedId] = useState<string | null>(null)

    // Price Popover state
    const [showPricePopover, setShowPricePopover] = useState(false)
    const [minPriceInput, setMinPriceInput] = useState(newParam.get('min_price') || '')
    const [maxPriceInput, setMaxPriceInput] = useState(newParam.get('max_price') || '')
    const pricePopoverRef = useRef<HTMLDivElement>(null)

    // Sync input states when query params change externally
    useEffect(() => {
        setSearchTerm(newParam.get('search') || '')
        setMinPriceInput(newParam.get('min_price') || '')
        setMaxPriceInput(newParam.get('max_price') || '')
    }, [newParam])

    // Close price popover on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (pricePopoverRef.current && !pricePopoverRef.current.contains(e.target as Node)) {
                setShowPricePopover(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Export products to CSV
    const exportData = useCallback(async (startDate: number, endDate: number) => {
        try {
            const apiRes = await henceforthApi.Product.export(startDate, endDate)
            const data = apiRes.data.data
            const rows = [
                [
                    "Sr.",
                    "Product Id",
                    "Category Level 1",
                    "Category level 2",
                    "Brand",
                    "Product Name",
                    "Product Price",
                    "Quantity",
                ],
            ];

            if (Array.isArray(data)) {
                data.forEach((res: any, index: number) => {
                    rows.push([
                        String(index + 1),
                        res?.prodct_id || '',
                        res?.category_id?.name || '',
                        res?.subcategory_id?.name || '',
                        res?.brand_id?.name || '',
                        res?.name || '',
                        res?.price ? String(res.price) : '0',
                        res?.quantity !== undefined ? String(res.quantity) : '0',
                    ])
                })
            }

            let csvContent = "data:text/csv;charset=utf-8," + rows.map((e) => e.join(",")).join("\n");
            var encodedUri = encodeURI(csvContent);
            var link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", `products_${moment().valueOf()}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            let closeModal = document.getElementById("closeModal");
            if (closeModal) {
                closeModal.click();
            }
        } catch (error) {
            handleError(error, 'active', authDispatch);
        }
    }, [authDispatch])

    const copyText = useCallback((id: string) => {
        if (id) {
            navigator?.clipboard?.writeText(id)
            setCopiedId(id)
            setTimeout(() => setCopiedId(null), 2000)
            toast.success(
                `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_id || 'ID'} ${language_data?.[authState.lang]?.common_copy || 'copied'} ${language_data?.[authState.lang]?.common_successfull || 'successfully'}`
            )
        }
    }, [authState.lang, language_data])

    // Fetch live catalog products list
    const productListing = async () => {
        setLoading(true)
        try {
            const pageIndex = Math.max(0, Number(match?.params.page || 1) - 1)
            let res = (await henceforthApi.Product.getProductList('', pageIndex, Limit, newParam.toString())).data
            setProductList({
                totalCount: res.total_count ?? 0,
                listing: Array.isArray(res.data) ? res.data : [],
            })
        } catch (err: any) {
            if (newParam.has('product_id')) {
                toast.warn(
                    `${language_data?.[authState.lang]?.common_please || 'Please'} ${language_data?.[authState.lang]?.common_search || 'search'} ${language_data?.[authState.lang]?.common_proper || 'proper'} ${language_data?.[authState.lang]?.common_id || 'Product ID'}`
                )
                setProductList({
                    totalCount: 0,
                    listing: [],
                })
            }
            handleError(err, '', authDispatch);
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        productListing();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [match?.params.page, location.search])

    // Search input submission handlers
    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        handleSearch('search', searchTerm.trim())
    }

    const handleClearSearch = () => {
        setSearchTerm('')
        handleSearch('search', '')
    }

    const handleApplyPrice = (e: React.FormEvent) => {
        e.preventDefault()
        if (minPriceInput && maxPriceInput && Number(minPriceInput) > Number(maxPriceInput)) {
            toast.warn('Max price should be greater than Min price')
            return
        }
        onFilterPriceHandler(minPriceInput, maxPriceInput)
        setShowPricePopover(false)
    }

    const handleClearPrice = () => {
        setMinPriceInput('')
        setMaxPriceInput('')
        onFilterPriceHandler('', '')
        setShowPricePopover(false)
    }

    const clearAllFilters = () => {
        setSearchTerm('')
        setMinPriceInput('')
        setMaxPriceInput('')
        navigate({
            pathname: '/products/1',
            search: '',
        })
    }

    // Calculate Active Filters
    const hasSearch = Boolean(newParam.get('search'))
    const hasProductId = Boolean(newParam.get('product_id'))
    const hasStock = Boolean(newParam.get('stock'))
    const hasPrice = Boolean(newParam.get('min_price') && newParam.get('max_price'))
    const activeFiltersCount = [hasSearch, hasProductId, hasStock, hasPrice].filter(Boolean).length

    // Column Definitions for AG Grid
    const productColumns = useMemo<ColDef[]>(() => [
        {
            headerName: `${language_data?.[authState.lang]?.common_sr_no || 'Sr.No.'}`,
            field: 'sr_no',
            width: 80,
            minWidth: 70,
            maxWidth: 90,
            valueGetter: (params) => {
                const pageNum = Number(match?.params.page) || 1;
                return (pageNum - 1) * Limit + (params.node?.rowIndex ?? 0) + 1;
            },
        },
        {
            headerName: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_id || 'ID'}`,
            field: 'prodct_id',
            minWidth: 150,
            width: 160,
            cellRenderer: (params: any) => {
                const id = params.data?.prodct_id || `${language_data?.[authState.lang]?.common_not_available || 'N/A'}`;
                const isCopied = copiedId === params.data?.prodct_id;
                return (
                    <div className="flex items-center gap-2 h-full">
                        <span className="font-semibold text-slate-800 tracking-tight text-xs bg-slate-100/90 px-2.5 py-1 rounded-md border border-slate-200/80 font-mono">
                            #{id}
                        </span>
                        {params.data?.prodct_id && (
                            <button
                                type="button"
                                onClick={() => copyText(params.data.prodct_id)}
                                className="p-1 hover:bg-sky-50 rounded text-slate-400 hover:text-[#1AA7DE] transition-colors cursor-pointer"
                                title={isCopied ? "Copied!" : "Copy Product ID"}
                                aria-label="Copy Product ID"
                            >
                                {isCopied ? (
                                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                                ) : (
                                    <Copy className="w-3.5 h-3.5" />
                                )}
                            </button>
                        )}
                    </div>
                );
            },
        },
        {
            headerName: `${language_data?.[authState.lang]?.common_product || 'Product'} Name`,
            field: 'name',
            minWidth: 260,
            flex: 1.8,
            cellRenderer: (params: any) => {
                const pic = Array.isArray(params.data?.images) && params.data.images.length
                    ? `${henceforthApi.API_FILE_ROOT_SMALL}${params.data.images[0]}`
                    : laptop;
                const name = params.data?.name || language_data?.[authState.lang]?.common_not_available || 'N/A';
                const category = params.data?.category_id?.name || '';
                return (
                    <ProductCell
                        image={pic}
                        fallbackImage={laptop}
                        name={name}
                        subtitle={category}
                        sku={params.data?.sku}
                    />
                );
            },
        },
        {
            headerName: `${language_data?.[authState.lang]?.common_category_level || 'Category'} 1`,
            field: 'category_id.name',
            minWidth: 140,
            flex: 1,
            cellRenderer: (params: any) => {
                const catName = params.data?.category_id?.name;
                if (!catName) {
                    return <span className="text-slate-400">—</span>;
                }
                return (
                    <div className="flex items-center h-full">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200/60 truncate max-w-[150px]">
                            {catName}
                        </span>
                    </div>
                );
            },
        },
        {
            headerName: `${language_data?.[authState.lang]?.common_category_level || 'Category'} 2`,
            field: 'subcategory_id.name',
            minWidth: 140,
            flex: 1,
            cellRenderer: (params: any) => {
                const subCat = params.data?.subcategory_id?.name;
                return (
                    <div className="flex items-center h-full text-xs font-medium text-slate-600">
                        {subCat || '—'}
                    </div>
                );
            },
        },
        {
            headerName: `${language_data?.[authState.lang]?.common_brand || 'Brand'}`,
            field: 'brand_id.name',
            minWidth: 120,
            flex: 0.9,
            cellRenderer: (params: any) => {
                const brand = params.data?.brand_id?.name;
                return (
                    <div className="flex items-center h-full text-xs font-semibold text-slate-700">
                        {brand || '—'}
                    </div>
                );
            },
        },
        {
            headerName: `${language_data?.[authState.lang]?.common_price || 'Price'}`,
            field: 'price',
            minWidth: 110,
            cellRenderer: (params: any) => (
                <div className="flex items-center h-full font-black text-slate-900 text-sm">
                    ${numberWithCommas(params.data?.price || 0)}
                </div>
            ),
        },
        {
            headerName: `${language_data?.[authState.lang]?.common_quantity || 'Stock Status'}`,
            field: 'quantity',
            width: 150,
            minWidth: 140,
            cellRenderer: (params: any) => (
                <div className="flex items-center h-full">
                    <StockBadge
                        quantity={params.data?.quantity}
                        outOfStockText={language_data?.[authState.lang]?.common_out_of_stock || 'Out of Stock'}
                    />
                </div>
            ),
        },
        {
            headerName: `${language_data?.[authState.lang]?.common_action || 'Action'}`,
            field: '_id',
            width: 105,
            minWidth: 95,
            sortable: false,
            filter: false,
            pinned: 'right',
            cellRenderer: (params: any) => {
                const isBlocked = authState?.is_admin_verified === 'pending' || authState?.is_admin_verified === 'reject';
                const handleBlocked = (e: React.MouseEvent) => {
                    e.preventDefault();
                    if (authState?.is_admin_verified === 'pending') {
                        toast.error("Your account is pending for admin verification.");
                    } else if (authState?.is_admin_verified === 'reject') {
                        toast.error("Your account is rejected for admin verification.");
                    }
                };

                return (
                    <div className="flex items-center h-full">
                        <ActionCell
                            viewUrl={`/product/${params.data?._id}`}
                            editUrl={`/product/${params.data?._id}/edit`}
                            isBlocked={isBlocked}
                            onView={isBlocked ? handleBlocked : undefined}
                            onEdit={isBlocked ? handleBlocked : undefined}
                        />
                    </div>
                );
            },
        },
    ], [authState, language_data, match?.params.page, copiedId, copyText]);

    const handleAddProductClick = (e: React.MouseEvent) => {
        if (authState?.is_admin_verified === 'pending') {
            e.preventDefault();
            toast.error("Your account is pending for admin verification.");
        } else if (authState?.is_admin_verified === 'reject') {
            e.preventDefault();
            toast.error("Your account is rejected for admin verification.");
        }
    };

    return (
        <Fragment>
            {/* Standard Breadcrumb Bar */}
            <BreadCrumb pathNameDeclare={breadCrumbPath} />

            <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-4">

                {/* 1. Page Header Card with Title, Status & Action Buttons */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white px-5 py-4 rounded-2xl border border-slate-200/80 shadow-xs">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight m-0">
                                {language_data?.[authState.lang]?.common_product || 'Products'} Catalog
                            </h1>
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-sky-50 text-[#1AA7DE] border border-sky-200/60 shadow-2xs">
                                {productList.totalCount} {productList.totalCount === 1 ? 'Item' : 'Items'}
                            </span>
                        </div>
                        <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1 mb-0">
                            Monitor inventory levels, update store catalog items, and track product pricing.
                        </p>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                        {/* Export CSV Button */}
                        <button
                            type="button"
                            data-bs-toggle="modal"
                            data-bs-target="#fileDownloadModal"
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs sm:text-sm shadow-2xs transition-all active:scale-[0.98]"
                        >
                            <Download className="w-4 h-4 text-slate-500" />
                            <span>{language_data?.[authState.lang]?.common_export_file || 'Export CSV'}</span>
                        </button>

                        {/* Add Product Button */}
                        <Link
                            to={authState?.is_admin_verified === 'pending' || authState?.is_admin_verified === 'reject' ? "#" : "/product/add"}
                            onClick={handleAddProductClick}
                            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#1AA7DE] hover:bg-[#158ebf] text-white font-bold text-xs sm:text-sm shadow-sm transition-all active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1AA7DE]"
                        >
                            <Plus className="w-4 h-4" />
                            <span>{language_data?.[authState.lang]?.common_add || 'Add Product'}</span>
                        </Link>
                    </div>
                </div>
                {/* 3. Comprehensive Modern Filter & Search Toolbar */}
                <div className="bg-white p-4 rounded-2xl border border-slate-100 shadow-sm space-y-4">
                    <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 flex-wrap">

                        {/* Search by Product Name */}
                        <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[240px]">
                            <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search by product name..."
                                className="w-[300px] pl-10 pr-9 py-2.5 text-xs sm:text-sm font-medium bg-slate-50/80 border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all"
                            />
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={handleClearSearch}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </form>

                        {/* Price Range Filter Popover */}
                        <div className="relative" ref={pricePopoverRef}>
                            <button
                                type="button"
                                onClick={() => setShowPricePopover(!showPricePopover)}
                                className={`inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl border text-xs sm:text-sm font-semibold transition-all ${hasPrice
                                    ? 'border-[#1AA7DE] bg-sky-50 text-[#1AA7DE]'
                                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                                    }`}
                            >
                                <DollarSign className="w-4 h-4 text-slate-500" />
                                <span>
                                    {hasPrice
                                        ? `$${newParam.get('min_price')} - $${newParam.get('max_price')}`
                                        : 'Price Range'}
                                </span>
                                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                            </button>

                            {/* Dropdown Menu */}
                            {showPricePopover && (
                                <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 z-50 animate-in fade-in zoom-in-95">
                                    <div className="flex items-center justify-between pb-3 border-b border-slate-100 mb-3">
                                        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                            Filter by Price
                                        </span>
                                        {hasPrice && (
                                            <button
                                                type="button"
                                                onClick={handleClearPrice}
                                                className="text-xs text-[#1AA7DE] font-semibold hover:underline"
                                            >
                                                Clear
                                            </button>
                                        )}
                                    </div>

                                    <form onSubmit={handleApplyPrice} className="space-y-3">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div>
                                                <label className="text-[11px] font-bold text-slate-500 block mb-1">
                                                    Min ($)
                                                </label>
                                                <input
                                                    type="number"
                                                    value={minPriceInput}
                                                    onChange={(e) => setMinPriceInput(e.target.value)}
                                                    placeholder="0"
                                                    className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-[11px] font-bold text-slate-500 block mb-1">
                                                    Max ($)
                                                </label>
                                                <input
                                                    type="number"
                                                    value={maxPriceInput}
                                                    onChange={(e) => setMaxPriceInput(e.target.value)}
                                                    placeholder="1000"
                                                    className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]"
                                                />
                                            </div>
                                        </div>

                                        {/* Quick presets */}
                                        <div className="flex flex-wrap gap-1.5 pt-1">
                                            {[
                                                { label: '$0 - $50', min: '0', max: '50' },
                                                { label: '$50 - $200', min: '50', max: '200' },
                                                { label: '$200 - $1000', min: '200', max: '1000' },
                                            ].map((preset) => (
                                                <button
                                                    key={preset.label}
                                                    type="button"
                                                    onClick={() => {
                                                        setMinPriceInput(preset.min)
                                                        setMaxPriceInput(preset.max)
                                                    }}
                                                    className="px-2 py-1 rounded text-[11px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors"
                                                >
                                                    {preset.label}
                                                </button>
                                            ))}
                                        </div>

                                        <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                                            <button
                                                type="submit"
                                                className="flex-1 py-1.5 rounded-lg bg-[#1AA7DE] hover:bg-[#158ebf] text-white font-bold text-xs shadow-2xs transition-colors"
                                            >
                                                Apply Price
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setShowPricePopover(false)}
                                                className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold text-xs transition-colors"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Active Filter Chips Bar */}
                    {activeFiltersCount > 0 && (
                        <div className="pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap text-xs">
                            <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-slate-500">Active Filters:</span>

                                {hasSearch && (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-50 text-[#1AA7DE] font-semibold border border-sky-200/60">
                                        <span>Name: &ldquo;{newParam.get('search')}&rdquo;</span>
                                        <button type="button" onClick={handleClearSearch} className="hover:text-sky-800">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}

                                {hasStock && (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-50 text-[#1AA7DE] font-semibold border border-sky-200/60">
                                        <span>Stock: {newParam.get('stock')?.replace(/_/g, ' ')}</span>
                                        <button type="button" onClick={() => handleSearch('stock', '')} className="hover:text-sky-800">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}

                                {hasPrice && (
                                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-sky-50 text-[#1AA7DE] font-semibold border border-sky-200/60">
                                        <span>Price: ${newParam.get('min_price')} - ${newParam.get('max_price')}</span>
                                        <button type="button" onClick={handleClearPrice} className="hover:text-sky-800">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={clearAllFilters}
                                className="inline-flex items-center gap-1 text-slate-500 hover:text-rose-600 font-bold transition-colors ml-auto cursor-pointer"
                            >
                                <RotateCcw className="w-3.5 h-3.5" />
                                <span>Reset all filters</span>
                            </button>
                        </div>
                    )}
                </div>

                {/* 4. AG Grid Modern Table Container */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                    <AgTable
                        rowData={productList.listing || []}
                        columnDefs={productColumns}
                        loading={loading}
                        pagination={true}
                        page={Number(match?.params.page) || 1}
                        limit={Number(Limit)}
                        count={productList.totalCount}
                        emptyMessage={
                            activeFiltersCount > 0
                                ? 'No Products Match Selected Filters'
                                : language_data?.[authState.lang]?.common_no_data_found || 'No Products Found'
                        }
                        emptySubtitle={
                            activeFiltersCount > 0
                                ? 'Try removing some filters or search keywords to view your products.'
                                : 'Start adding catalog items to build your storefront inventory.'
                        }
                        emptyAction={
                            activeFiltersCount > 0 ? (
                                <button
                                    type="button"
                                    onClick={clearAllFilters}
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white hover:bg-slate-800 font-bold text-xs shadow-sm transition-all"
                                >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                    <span>Clear All Filters</span>
                                </button>
                            ) : (
                                <Link
                                    to="/product/add"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#1AA7DE] text-white hover:bg-[#158ebf] font-bold text-xs shadow-sm transition-all"
                                >
                                    <Plus className="w-3.5 h-3.5" />
                                    <span>Add First Product</span>
                                </Link>
                            )
                        }
                    />
                </div>
            </div>

            {/* Modal for file download */}
            <DownloadFileModal exportData={exportData} />
        </Fragment>
    )
}

export default Products;